import * as z from "zod";
import { PaymentMethodSchema, PixChargeSchema } from "./charges.js";
import { Id, IsoDate } from "./ids.js";
import { PlanIdSchema, PlanStateSchema, PromoSchema } from "./plans.js";

/** Billing as the customer sees it: one overview, invoices, plan changes, support. */

export const PlanIntervalSchema = z.enum(["monthly", "annual"]);

/**
 * Where the workspace stands.
 * - `free`: never subscribed and no trial running.
 * - `trialing`: the 7-day trial, no card.
 * - `active`: paid and in force (also while cancelAtPeriodEnd is true).
 * - `past_due`: a renewal failed; retries are running, work still goes on.
 * - `suspended`: retries ran out or an operator suspended it; agent runs pause.
 * - `canceled`: the paid term ended after a cancellation.
 */
export const BillingStateSchema = z.enum([
  "free",
  "trialing",
  "active",
  "past_due",
  "suspended",
  "canceled",
]);
export type BillingState = z.infer<typeof BillingStateSchema>;

export const TrialSchema = z.object({
  startedAt: IsoDate,
  endsAt: IsoDate,
  creditCents: z.number().int().nonnegative(),
  creditRemainingCents: z.number().int().nonnegative(),
});

export const BillingPaymentMethodSchema = z.object({
  type: z.enum(["credit_card", "pix", "boleto"]),
  brand: z.string().nullable(),
  last4: z.string().nullable(),
});

export const BillingOverviewSchema = z.object({
  plan: PlanStateSchema,
  state: BillingStateSchema,
  interval: PlanIntervalSchema,
  seats: z.number().int().positive().nullable(),
  trial: TrialSchema.nullable(),
  /** The workspace may still start the trial. */
  trialAvailable: z.boolean(),
  /** The plan ends at currentPeriodEndsAt instead of renewing. */
  cancelAtPeriodEnd: z.boolean(),
  currentPeriodEndsAt: IsoDate.nullable(),
  /** What the next renewal will charge; reflects the promotion ending. */
  nextChargeCents: z.number().int().nonnegative().nullable(),
  nextChargeAt: IsoDate.nullable(),
  /** The price now being paid per term, in centavos. */
  currentPriceCents: z.number().int().nonnegative().nullable(),
  promo: PromoSchema.nullable(),
  paymentMethod: z
    .object({
      type: z.enum(["credit_card", "pix", "boleto"]),
      brand: z.string().nullable(),
      last4: z.string().nullable(),
    })
    .nullable(),
  /** A downgrade that takes effect at the end of the period. */
  pendingChange: z
    .object({
      planId: PlanIdSchema,
      interval: PlanIntervalSchema,
      seats: z.number().int().positive().nullable(),
      effectiveAt: IsoDate,
    })
    .nullable(),
  /**
   * A plan the person chose and has not paid yet: the checkout is open and nothing is charged
   * until they pay. It expires by itself at `expiresAt`; `billing.cancelPending` ends it now.
   */
  awaitingPayment: z
    .object({
      planId: PlanIdSchema,
      interval: PlanIntervalSchema,
      amountCents: z.number().int().nonnegative(),
      checkoutUrl: z.string().url().nullable(),
      since: IsoDate,
      expiresAt: IsoDate,
    })
    .nullable()
    .optional(),
  /** Present while `past_due` or `suspended` for a failed payment. */
  delinquency: z
    .object({
      since: IsoDate,
      attempts: z.number().int().nonnegative(),
      nextRetryAt: IsoDate.nullable(),
      suspendsAt: IsoDate.nullable(),
    })
    .nullable(),
  /** Prepaid credit (top-ups and comps), BRL centavos. */
  creditBalanceCents: z.number().int().nonnegative(),
});
export type BillingOverview = z.infer<typeof BillingOverviewSchema>;

export const BillingInvoiceSchema = z.object({
  id: Id,
  kind: z.enum(["plan", "top_up", "other"]),
  /** Set for kind "plan"; null for top-ups. */
  planId: PlanIdSchema.nullable().default(null),
  interval: PlanIntervalSchema.nullable().default(null),
  description: z.string(),
  status: z.enum(["pending", "paid", "overdue", "refunded", "canceled", "failed"]),
  amountCents: z.number().int().nonnegative(),
  refundedCents: z.number().int().nonnegative(),
  method: PaymentMethodSchema.or(z.literal("boleto")).nullable(),
  issuedAt: IsoDate,
  dueAt: IsoDate.nullable(),
  paidAt: IsoDate.nullable(),
  /** The provider's page to pay or view the invoice. */
  invoiceUrl: z.string().url().nullable(),
  receiptUrl: z.string().url().nullable(),
  /** The NFS-e PDF, once issued. */
  nfseUrl: z.string().url().nullable(),
});
export type BillingInvoice = z.infer<typeof BillingInvoiceSchema>;

export const ChangePlanInputSchema = z.object({
  planId: PlanIdSchema,
  interval: PlanIntervalSchema.default("monthly"),
  seats: z.number().int().positive().max(1000).optional(),
  /** Preferred way to pay when a payment is needed. `any` lets the payer choose on the hosted page. */
  method: z.enum(["any", "pix", "credit_card", "boleto"]).default("any"),
  clientNonce: z.string().min(1).max(200).optional(),
});

/**
 * - `applied`: in force now (an upgrade charged the prorated difference, or no charge was due).
 * - `scheduled`: a downgrade that starts at `effectiveAt`.
 * - `payment_required`: finish paying at `checkoutUrl` (hosted page) or with `pix`.
 */
export const ChangePlanResultSchema = z.object({
  outcome: z.enum(["applied", "scheduled", "payment_required"]),
  effectiveAt: IsoDate,
  amountCents: z.number().int().nonnegative(),
  checkoutUrl: z.string().url().nullable(),
  pix: PixChargeSchema.nullable(),
  overview: BillingOverviewSchema,
});

export const StartTrialInputSchema = z.object({
  /** CPF or CNPJ; its keyed hash decides "one trial per person". */
  taxId: z.string().trim().min(11).max(18).optional(),
  /** E.164 phone already verified for this account. */
  phone: z.string().trim().min(8).max(20).optional(),
});

/** Error codes the billing procedures carry in `data.code`. */
export const BILLING_ERROR_CODES = [
  "trial_already_used",
  "trial_needs_verification",
  "plan_not_on_sale",
  "account_suspended",
  "rate_limited",
] as const;

export const SupportTicketStatusSchema = z.enum(["open", "pending", "solved"]);
export const SupportMessageSchema = z.object({
  id: Id,
  author: z.enum(["customer", "operator"]),
  body: z.string(),
  createdAt: IsoDate,
});
export const SupportTicketSchema = z.object({
  id: Id,
  subject: z.string(),
  status: SupportTicketStatusSchema,
  createdAt: IsoDate,
  updatedAt: IsoDate,
  /** The customer has unread operator replies. */
  unread: z.boolean(),
});
export const SupportTicketDetailSchema = SupportTicketSchema.extend({
  messages: z.array(SupportMessageSchema),
});
export type SupportTicket = z.infer<typeof SupportTicketSchema>;
export type SupportTicketDetail = z.infer<typeof SupportTicketDetailSchema>;
